import { format } from 'date-fns';
import {
  ColumnMapping,
  convertExcelDate,
  ExcelProcessorConfig,
  HeaderConfig,
  parseBoolean,
  ParsedExcelData,
  parseNumber,
  ValidationError
} from '@/lib/excel';
import { generateSoChungTuHienTai } from '@/lib/stringUtil';
import { roundNumber } from '@/lib/formatUtils';
import { MA_CHUNG_TU } from '@/constants';

const HEADERS = {
  // Main fields
  MA_KH: 'Mã nhà cung cấp*',
  TEN_KH_THUE: 'Tên nhà cung cấp',
  ONG_BA: 'Người nhận',
  SO_CT: 'Số chứng từ*',
  DIA_CHI: 'Địa chỉ',
  NGAY_CT: 'Ngày chứng từ*',
  MA_NX: 'Mã nhập xuất*',
  MA_TT: 'Hạn thanh toán',
  TK: 'Tài khoản nợ*',
  DIEN_GIAI: 'Diễn giải',
  NGAY_CT0: 'Ngày hóa đơn',
  MA_NT: 'Ngoại tệ*',
  TY_GIA: 'Tỷ giá',
  MA_GD: 'Giao dịch*',
  CHUYEN_DU_LIEU: 'Chuyển dữ liệu',
  // Detail fields
  MA_VT: 'Mã sản phẩm*',
  DVT: 'Đvt*',
  MA_KHO: 'Mã kho*',
  MA_LO: 'Mã lô',
  MA_VI_TRI: 'Mã vị trí',
  SO_LUONG: 'Số lượng*',
  GIA_NT: 'Giá',
  TIEN_NT: 'Tiền',
  THUE_NT: 'Thuế',
  TK_CO: 'Tk có*',
  MA_BP: 'Bộ phận',
  MA_VV: 'Vụ việc',
  MA_HD: 'Hợp đồng',
  MA_DTT: 'Đợt thanh toán',
  MA_KU: 'Khế ước',
  MA_PHI: 'Phí',
  MA_SP: 'Sản phẩm',
  MA_LSX: 'Lệnh sản xuất',
  MA_CP0: 'C/p không h/lệ'
} as const;

const HEADER_CONFIG: HeaderConfig[] = [
  // Main fields
  { header: HEADERS.MA_KH, key: 'ma_kh', type: 'main' },
  { header: HEADERS.TEN_KH_THUE, key: 'ten_kh_thue', type: 'main' },
  { header: HEADERS.ONG_BA, key: 'ong_ba', type: 'main' },
  { header: HEADERS.SO_CT, key: 'so_ct', type: 'main' },
  { header: HEADERS.DIA_CHI, key: 'dia_chi', type: 'main' },
  { header: HEADERS.NGAY_CT, key: 'ngay_ct', type: 'main' },
  { header: HEADERS.MA_NX, key: 'ma_nx', type: 'main' },
  { header: HEADERS.MA_TT, key: 'ma_tt', type: 'main' },
  { header: HEADERS.TK, key: 'tk', type: 'main' },
  { header: HEADERS.DIEN_GIAI, key: 'dien_giai', type: 'main' },
  { header: HEADERS.NGAY_CT0, key: 'ngay_ct0', type: 'main' },
  { header: HEADERS.MA_NT, key: 'ma_nt', type: 'main' },
  { header: HEADERS.TY_GIA, key: 'ty_gia', type: 'main' },
  { header: HEADERS.MA_GD, key: 'ma_gd', type: 'main' },
  { header: HEADERS.CHUYEN_DU_LIEU, key: 'transfer_yn', type: 'main' },
  // Detail fields
  { header: HEADERS.MA_VT, key: 'ma_vt', type: 'chi_tiet' },
  { header: HEADERS.DVT, key: 'dvt', type: 'chi_tiet' },
  { header: HEADERS.MA_KHO, key: 'ma_kho', type: 'chi_tiet' },
  { header: HEADERS.MA_LO, key: 'ma_lo', type: 'chi_tiet' },
  { header: HEADERS.MA_VI_TRI, key: 'ma_vi_tri', type: 'chi_tiet' },
  { header: HEADERS.SO_LUONG, key: 'so_luong', type: 'chi_tiet' },
  { header: HEADERS.GIA_NT, key: 'gia_nt', type: 'chi_tiet' },
  { header: HEADERS.TIEN_NT, key: 'tien_nt', type: 'chi_tiet' },
  { header: HEADERS.THUE_NT, key: 'thue_nt', type: 'chi_tiet' },
  { header: HEADERS.TK_CO, key: 'tk_vt', type: 'chi_tiet' },
  { header: HEADERS.MA_BP, key: 'ma_bp', type: 'chi_tiet' },
  { header: HEADERS.MA_VV, key: 'ma_vv', type: 'chi_tiet' },
  { header: HEADERS.MA_HD, key: 'ma_hd', type: 'chi_tiet' },
  { header: HEADERS.MA_DTT, key: 'ma_dtt', type: 'chi_tiet' },
  { header: HEADERS.MA_KU, key: 'ma_ku', type: 'chi_tiet' },
  { header: HEADERS.MA_PHI, key: 'ma_phi', type: 'chi_tiet' },
  { header: HEADERS.MA_SP, key: 'ma_sp', type: 'chi_tiet' },
  { header: HEADERS.MA_LSX, key: 'ma_lsx', type: 'chi_tiet' },
  { header: HEADERS.MA_CP0, key: 'ma_cp0', type: 'chi_tiet' }
];

const UUID_FIELDS = {
  main: ['ma_nt', 'ma_kh', 'ma_nx', 'tk', 'ma_tt', 'ma_gd'],
  chi_tiet: [
    'ma_vt',
    'dvt',
    'ma_kho',
    'ma_lo',
    'ma_vi_tri',
    'tk_vt',
    'ma_bp',
    'ma_vv',
    'ma_hd',
    'ma_dtt',
    'ma_ku',
    'ma_phi',
    'ma_cp0'
  ]
};

const getFieldName = (key: string) => {
  const config = HEADER_CONFIG.find(c => c.key === key);
  return config?.header?.replace('*', '').trim() || key;
};

const getFieldValues = <T>(...candidates: Array<T[] | undefined>): T[] => {
  for (const candidate of candidates) {
    if (Array.isArray(candidate) && candidate.length > 0) {
      const hasMeaningfulValue = candidate.some(
        value => value !== undefined && value !== null && String(value).trim() !== ''
      );
      if (hasMeaningfulValue) {
        return candidate;
      }
    }
  }
  return [];
};

const hasData = (item: Record<string, MuaHangRecord>, keys: string[]) =>
  keys.some(k => item[k] != null && item[k] !== '' && item[k] !== 0);

const UUIDPayload = (data: ParsedExcelData) => {
  const build = (section: Record<string, MuaHangRecord[]>, fields: string[]) =>
    Object.fromEntries(fields.map(f => [f, section?.[f] || []]));

  const main = data.main || {};
  const chi_tiet = data.chi_tiet || {};

  const seenDates = new Set<string>();
  const chungTuArr: Array<{ ma_ct: string; ngay_hl: string }> = [];

  (main.so_ct || []).forEach((_: MuaHangRecord, index: number) => {
    const ngay =
      convertExcelDate(main.ngay_lct?.[index]) ||
      convertExcelDate(main.ngay_ct?.[index]) ||
      format(new Date(), 'yyyy-MM-dd');

    const ngay_hl = ngay || format(new Date(), 'yyyy-MM-dd');
    if (seenDates.has(ngay_hl)) return;
    seenDates.add(ngay_hl);

    chungTuArr.push({
      ma_ct: MA_CHUNG_TU.MUA_HANG.PHIEU_XUAT_TRA_LAI_NHA_CUNG_CAP,
      ngay_hl
    });
  });

  return {
    main: {
      ...build(main, UUID_FIELDS.main)
    },
    chi_tiet: build(chi_tiet, UUID_FIELDS.chi_tiet),
    chung_tu: chungTuArr
  };
};

const ValidateUUID = (response: MuaHangRecord, columnMapping: Record<string, ColumnMapping>): ValidationError[] => {
  if (!response) return [];
  const errors: ValidationError[] = [];

  (['main', 'chi_tiet'] as const).forEach(section => {
    const data = response[section];
    if (!data) return;

    Object.entries(data).forEach(([field, values]) => {
      if (!Array.isArray(values)) return;
      const mapping = Object.values(columnMapping).find(m => m.key === field && m.type === section);

      values.forEach((val, i) => {
        if (val === null) {
          errors.push({
            row: i + 2,
            field,
            message: `${getFieldName(field)} không hợp lệ`,
            section,
            columnIndex: mapping?.index
          });
        }
      });
    });
  });

  return errors;
};

const calculateTotals = (chiTiet: ImportBody['chi_tiet']) => {
  let t_so_luong = 0;
  let t_tien_nt = 0;
  let t_tien = 0;
  let t_thue_nt = 0;
  let t_thue = 0;

  chiTiet.forEach(item => {
    t_so_luong = roundNumber(t_so_luong + (item.so_luong || 0), 6);
    t_tien_nt = roundNumber(t_tien_nt + (item.tien_nt || 0), 6);
    t_tien = roundNumber(t_tien + (item.tien_nt || 0), 6);
    t_thue_nt = roundNumber(t_thue_nt + (item.thue_nt || 0), 6);
    t_thue = roundNumber(t_thue + (item.thue_nt || 0), 6);
  });

  const t_tt_nt = roundNumber(t_tien_nt + t_thue_nt, 6);
  const t_tt = roundNumber(t_tien + t_thue, 6);

  return {
    t_so_luong: roundNumber(t_so_luong, 6),
    t_tien_nt: roundNumber(t_tien_nt, 6),
    t_tien: roundNumber(t_tien, 6),
    t_thue_nt: roundNumber(t_thue_nt, 6),
    t_thue: roundNumber(t_thue, 6),
    t_tt_nt: roundNumber(t_tt_nt, 6),
    t_tt: roundNumber(t_tt, 6)
  };
};

const processMainRows = (
  main: Record<string, MuaHangRecord[]>,
  UUIDResponse: MuaHangRecord,
  entityUnitUUID: string,
  rowCount: number
): Map<string, ImportBody> => {
  const groupedRecords = new Map<string, ImportBody>();
  const voucherCounters = new Map<string, number>();
  const voucherArray = Array.isArray(UUIDResponse?.chung_tu)
    ? UUIDResponse.chung_tu
    : UUIDResponse?.chung_tu
      ? [UUIDResponse.chung_tu]
      : [];

  const voucherByDate = new Map<string, MuaHangRecord>();
  voucherArray.forEach((voucher: MuaHangRecord) => {
    const normalizedDate = voucher?.ngay_hl?.slice(0, 10);
    if (!normalizedDate) return;
    const existing = voucherByDate.get(normalizedDate);
    if (!existing || Number(voucher.i_so_ct_ht ?? 0) > Number(existing.i_so_ct_ht ?? 0)) {
      voucherByDate.set(normalizedDate, voucher);
    }
  });

  for (let i = 0; i < rowCount; i++) {
    const documentNumber = main.so_ct?.[i] || '';
    const recordKey = `${documentNumber}`;

    if (!groupedRecords.has(recordKey)) {
      const isTransfer = parseBoolean(main.transfer_yn?.[i]);

      const transactionDate = convertExcelDate(main.ngay_lct?.[i]) || convertExcelDate(main.ngay_ct?.[i]);
      const documentDate = convertExcelDate(main.ngay_ct?.[i]) || transactionDate;
      const effectiveDate = transactionDate || documentDate || format(new Date(), 'yyyy-MM-dd');

      const voucherInfo = voucherByDate.get(effectiveDate) || voucherArray[i] || voucherArray[0];

      const counterKey = `${voucherInfo?.chung_tu || voucherInfo?.ma_ct || 'default'}_${effectiveDate}`;
      const base = voucherInfo?.i_so_ct_ht !== undefined ? Number(voucherInfo.i_so_ct_ht) : 0;
      const nextNumber = voucherCounters.has(counterKey) ? voucherCounters.get(counterKey)! : base + 1;
      voucherCounters.set(counterKey, nextNumber + 1);
      const nextVoucherNumber = voucherInfo ? nextNumber : undefined;
      const generatedVoucherNumber =
        voucherInfo?.so_ct_mau && nextVoucherNumber
          ? generateSoChungTuHienTai(nextVoucherNumber, voucherInfo.so_ct_mau, effectiveDate)
          : undefined;

      groupedRecords.set(recordKey, {
        so_ct: documentNumber || generatedVoucherNumber || undefined,
        ngay_ct: transactionDate || documentDate,
        ngay_lct: transactionDate || documentDate,
        ngay_ct0: convertExcelDate(main.ngay_ct0?.[i]) || transactionDate || documentDate,
        i_so_ct: nextVoucherNumber || 0,
        ma_nt: UUIDResponse.main?.ma_nt?.[i] || main.ma_nt?.[i] || '',
        ty_gia: main.ty_gia?.[i] || 1,
        ma_kh: UUIDResponse.main?.ma_kh?.[i] || '',
        ten_kh_thue: main.ten_kh_thue?.[i] || '',
        ma_so_thue: main.ma_so_thue?.[i] || '',
        dia_chi: main.dia_chi?.[i] || '',
        ong_ba: main.ong_ba?.[i] || '',
        dien_giai: (main.dien_giai?.[i]?.trim?.() || '') as string,
        ma_nx: UUIDResponse.main?.ma_nx?.[i] || '',
        tk: UUIDResponse.main?.tk?.[i] || '',
        ma_tt: UUIDResponse.main?.ma_tt?.[i] || undefined,
        ma_tthddt: main.ma_tthddt?.[i] || '0',
        so_ct_hddt: main.so_ct_hddt?.[i] || '',
        ngay_ct_hddt: convertExcelDate(main.ngay_ct_hddt?.[i]) || '',
        so_ct2_hddt: main.so_ct2_hddt?.[i] || '',
        ma_mau_ct_hddt: main.ma_mau_ct_hddt?.[i] || '',
        transfer_yn: isTransfer,
        unit_id: entityUnitUUID,
        ma_ngv: '3',
        ma_gd: UUIDResponse.main?.ma_gd?.[i] || '',
        status: '5',
        e_mail: main.e_mail?.[i] || '',
        ma_mau_bc: '5',
        thue_suat: 0,
        t_so_luong: 0,
        t_tien_nt: 0,
        t_tien: 0,
        t_thue_nt: 0,
        t_thue: 0,
        t_tt_nt: 0,
        t_tt: 0,
        chi_tiet: []
      });
    }
  }

  return groupedRecords;
};

const processChiTietRows = (
  groupedRecords: Map<string, ImportBody>,
  main: Record<string, MuaHangRecord[]>,
  chi_tiet: Record<string, MuaHangRecord[]>,
  UUIDResponse: MuaHangRecord,
  rowCount: number
): Map<string, ImportBody['chi_tiet']> => {
  const chiTietMap = new Map<string, ImportBody['chi_tiet']>();
  const detailKeys = HEADER_CONFIG.filter(c => c.type === 'chi_tiet').map(c => c.key);

  for (let i = 0; i < rowCount; i++) {
    const documentNumber = main.so_ct?.[i] || '';
    const recordKey = `${documentNumber}`;

    const record = groupedRecords.get(recordKey);
    if (!record) continue;

    const quantity = roundNumber(chi_tiet.so_luong?.[i], 6);
    const price = roundNumber(chi_tiet.gia_nt?.[i], 6);
    const amount = roundNumber(chi_tiet.tien_nt?.[i] || quantity * price, 6);
    const taxAmount = roundNumber(chi_tiet.thue_nt?.[i], 6);

    const detailsForKey = chiTietMap.get(recordKey) || [];

    const detailItem = {
      line: detailsForKey.length + 1,
      ma_vt: UUIDResponse.chi_tiet?.ma_vt?.[i] || '',
      dvt: UUIDResponse.chi_tiet?.dvt?.[i] || '',
      ma_kho: UUIDResponse.chi_tiet?.ma_kho?.[i] || '',
      ma_lo: UUIDResponse.chi_tiet?.ma_lo?.[i] || '',
      ma_vi_tri: UUIDResponse.chi_tiet?.ma_vi_tri?.[i] || chi_tiet.ma_vi_tri?.[i] || '',
      he_so: 1,
      so_luong: quantity,
      gia_nt: price,
      tien_nt: amount,
      gia: price,
      tien: amount,
      thue_nt: taxAmount,
      thue: taxAmount,
      tk_vt: UUIDResponse.chi_tiet?.tk_vt?.[i] || '',
      ma_bp: UUIDResponse.chi_tiet?.ma_bp?.[i] || '',
      ma_vv: UUIDResponse.chi_tiet?.ma_vv?.[i] || '',
      ma_hd: UUIDResponse.chi_tiet?.ma_hd?.[i] || '',
      ma_dtt: UUIDResponse.chi_tiet?.ma_dtt?.[i] || '',
      ma_ku: UUIDResponse.chi_tiet?.ma_ku?.[i] || '',
      ma_phi: UUIDResponse.chi_tiet?.ma_phi?.[i] || '',
      ma_cp0: UUIDResponse.chi_tiet?.ma_cp0?.[i] || ''
    };

    if (hasData(detailItem, detailKeys)) {
      detailsForKey.push(detailItem);
      chiTietMap.set(recordKey, detailsForKey);
    }
  }

  return chiTietMap;
};

const TransformToBodies = (
  dataRows: MuaHangRecord[][],
  parsedData: ParsedExcelData,
  uuidResponse: MuaHangRecord,
  context: { entityUnitUUID: string; defaultMaGD?: string }
): ImportBody[] => {
  const main = parsedData.main || {};
  const chi_tiet = parsedData.chi_tiet || {};
  const rowCount = dataRows.length;

  const groupedRecords = processMainRows(main, uuidResponse, context.entityUnitUUID, rowCount);
  const chiTietMap = processChiTietRows(groupedRecords, main, chi_tiet, uuidResponse, rowCount);

  const records = Array.from(groupedRecords.values());
  records.forEach(record => {
    const key = `${record.so_ct || ''}`;
    record.chi_tiet = chiTietMap.get(key) || [];

    const totals = calculateTotals(record.chi_tiet);
    record.t_so_luong = totals.t_so_luong;
    record.t_tien_nt = totals.t_tien_nt;
    record.t_tien = totals.t_tien;
    record.t_thue_nt = totals.t_thue_nt;
    record.t_thue = totals.t_thue;
    record.t_tt_nt = totals.t_tt_nt;
    record.t_tt = totals.t_tt;
  });

  return records;
};

export interface ImportBody {
  so_ct?: string;
  ngay_ct?: string;
  ngay_lct?: string;
  ngay_ct0?: string;
  ma_nt?: string;
  ty_gia?: number;
  ma_kh: string;
  ten_kh_thue?: string;
  ma_so_thue?: string;
  dia_chi?: string;
  ong_ba?: string;
  dien_giai?: string;
  ma_nx?: string;
  tk?: string;
  ma_tt?: string;
  ma_tthddt?: string;
  so_ct_hddt?: string;
  ngay_ct_hddt?: string;
  so_ct2_hddt?: string;
  ma_mau_ct_hddt?: string;
  transfer_yn?: boolean;
  unit_id: string;
  ma_ngv: string;
  ma_gd?: string;
  status?: string;
  i_so_ct?: number;
  e_mail?: string;
  ma_mau_bc?: string;
  thue_suat?: number;
  t_so_luong?: number;
  t_tien_nt?: number;
  t_tien?: number;
  t_thue_nt?: number;
  t_thue?: number;
  t_tt_nt?: number;
  t_tt?: number;
  chi_tiet: Array<{
    line: number;
    ma_vt: string;
    dvt: string;
    ma_kho: string;
    ma_lo?: string;
    ma_vi_tri?: string;
    he_so: number;
    so_luong: number;
    gia_nt: number;
    tien_nt: number;
    gia: number;
    tien: number;
    thue_nt?: number;
    thue?: number;
    tk_vt?: string;
    ma_bp?: string;
    ma_vv?: string;
    ma_hd?: string;
    ma_dtt?: string;
    ma_ku?: string;
    ma_phi?: string;
    ma_cp0?: string;
  }>;
}

export const ExcelConfig: ExcelProcessorConfig<ImportBody> = {
  Headers: HEADERS,
  HeaderConfig: HEADER_CONFIG,
  Transform: TransformToBodies,
  UUIDPayload,
  ValidateUUID
};
